import { z } from 'zod';
export const ETATS_RESERVATION_CRENEAU = [
    'en_attente_email',
    'demandee',
    'confirmee',
    'refusee',
    'annulee',
    'expiree',
];
export const ETATS_ACTIFS = ['en_attente_email', 'demandee', 'confirmee'];
export const DUREE_ATTENTE_MS = 30 * 60_000;
export const DELAI_PURGE_ATTENTE_MS = 7 * 86_400_000;
const identifiant = z.string().regex(/^[a-f0-9]{24}$/i);
const texteBilingue = (max) => z.strictObject({ fr: z.string().trim().max(max), en: z.string().trim().max(max).default('') });
const heure = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/);
const version = z.number().int().min(0);
export const plageRdvSchema = z
    .strictObject({ jour: z.number().int().min(1).max(7), debut: heure, fin: heure })
    .refine((p) => p.debut < p.fin, 'La fin doit suivre le début.');
const plages = z
    .array(plageRdvSchema)
    .max(42)
    .superRefine((valeurs, ctx) => {
    for (let jour = 1; jour <= 7; jour++) {
        const lignes = valeurs
            .filter((p) => p.jour === jour)
            .sort((a, b) => a.debut.localeCompare(b.debut));
        if (lignes.length > 6 || lignes.some((p, i) => i > 0 && p.debut < lignes[i - 1].fin))
            ctx.addIssue({
                code: 'custom',
                message: 'Six plages au plus par jour, sans chevauchement.',
            });
    }
});
const champsType = z.strictObject({
    slug: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    titre: texteBilingue(200).refine((t) => t.fr.length > 0, 'Un titre français est requis.'),
    description: texteBilingue(1000).default({ fr: '', en: '' }),
    dureeMinutes: z.number().int().min(15).max(480).multipleOf(5),
    tamponAvantMinutes: z.number().int().min(0).max(120).default(0),
    tamponApresMinutes: z.number().int().min(0).max(120).default(0),
    pasMinutes: z.union([z.literal(15), z.literal(30), z.literal(60)]).default(30),
    preavisHeures: z.number().min(0).max(720).default(24),
    horizonJours: z.number().int().min(1).max(180).default(60),
    plages: plages.default([]),
    membres: z
        .array(identifiant)
        .min(1)
        .max(10)
        .refine((ids) => new Set(ids).size === ids.length, 'Membre répété.'),
    validation: z.enum(['automatique', 'manuelle']).default('manuelle'),
    lieu: z.string().trim().max(500).default(''),
    oeuvre: z.enum(['aucune', 'facultative']).default('aucune'),
    telephoneObligatoire: z.boolean().default(false),
    enLigne: z.boolean().default(false),
    ordre: z.number().int().min(0).default(0),
});
export const typeRdvCreationSchema = champsType;
export const typeRdvModificationSchema = z.strictObject({
    slug: champsType.shape.slug.optional(),
    titre: champsType.shape.titre.optional(),
    description: champsType.shape.description.removeDefault().optional(),
    dureeMinutes: champsType.shape.dureeMinutes.optional(),
    tamponAvantMinutes: champsType.shape.tamponAvantMinutes.removeDefault().optional(),
    tamponApresMinutes: champsType.shape.tamponApresMinutes.removeDefault().optional(),
    pasMinutes: champsType.shape.pasMinutes.removeDefault().optional(),
    preavisHeures: champsType.shape.preavisHeures.removeDefault().optional(),
    horizonJours: champsType.shape.horizonJours.removeDefault().optional(),
    plages: champsType.shape.plages.removeDefault().optional(),
    membres: champsType.shape.membres.optional(),
    validation: champsType.shape.validation.removeDefault().optional(),
    lieu: champsType.shape.lieu.removeDefault().optional(),
    oeuvre: champsType.shape.oeuvre.removeDefault().optional(),
    telephoneObligatoire: champsType.shape.telephoneObligatoire.removeDefault().optional(),
    enLigne: champsType.shape.enLigne.removeDefault().optional(),
    ordre: champsType.shape.ordre.removeDefault().optional(),
    version,
});
const champsException = z.strictObject({
    sorte: z.enum(['fermeture', 'ouverture']),
    du: z.iso.datetime({ offset: true }),
    au: z.iso.datetime({ offset: true }),
    typeIds: z.array(identifiant).max(100).default([]),
    note: z.string().trim().max(1000).default(''),
});
export const exceptionRdvCreationSchema = champsException.refine((e) => Date.parse(e.du) < Date.parse(e.au), 'La fin doit suivre le début.');
export const exceptionRdvModificationSchema = z.strictObject({
    sorte: champsException.shape.sorte.optional(),
    du: champsException.shape.du.optional(),
    au: champsException.shape.au.optional(),
    typeIds: champsException.shape.typeIds.removeDefault().optional(),
    note: champsException.shape.note.removeDefault().optional(),
    version,
});
export const fuseauRdvSchema = z
    .string()
    .max(100)
    .refine((valeur) => {
    try {
        new Intl.DateTimeFormat('fr', { timeZone: valeur });
        return true;
    }
    catch {
        return false;
    }
}, 'Fuseau IANA inconnu.');
export const reglagesRdvSchema = z.strictObject({
    fuseau: fuseauRdvSchema.default('Europe/Paris'),
    simultanesMax: z.number().int().min(1).max(100).default(1),
    annulationVisiteurHeures: z.number().min(0).max(720).default(2),
    messageConfirmation: texteBilingue(4000).default({ fr: '', en: '' }),
    surLeSite: z.boolean().default(false),
    notifierMembres: z.boolean().default(true),
});
export const reglagesRdvModificationSchema = reglagesRdvSchema.extend({ version });
export const versionRdvSchema = z.strictObject({ version });
export const gesteReservationSchema = versionRdvSchema.extend({
    message: z.string().trim().max(2000).default(''),
});
export const deplacementReservationSchema = versionRdvSchema.extend({
    debut: z.iso.datetime({ offset: true }),
    force: z.boolean().default(false),
});
const formats = new Map();
export function partiesLocales(instant, fuseau) {
    let format = formats.get(fuseau);
    if (!format) {
        format = new Intl.DateTimeFormat('en-CA', {
            timeZone: fuseau,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        });
        formats.set(fuseau, format);
    }
    const morceaux = format.formatToParts(instant);
    const lire = (cle) => morceaux.find((p) => p.type === cle).value;
    return {
        date: `${lire('year')}-${lire('month')}-${lire('day')}`,
        heure: `${lire('hour')}:${lire('minute')}`,
    };
}
/** Une heure absente ne réserve rien ; une heure doublée désigne sa première occurrence. */
export function instantLocal(date, heure, fuseau) {
    const naif = Date.parse(`${date}T${heure}:00Z`);
    if (!Number.isFinite(naif))
        return null;
    const decalages = new Set();
    for (const delta of [-36, 0, 36]) {
        const essai = new Date(naif + delta * 3_600_000);
        const locale = partiesLocales(essai, fuseau);
        decalages.add(Date.parse(`${locale.date}T${locale.heure}:00Z`) - essai.getTime());
    }
    const candidats = [...decalages]
        .map((d) => new Date(naif - d))
        .filter((d) => {
        const p = partiesLocales(d, fuseau);
        return p.date === date && p.heure === heure;
    })
        .sort((a, b) => a.getTime() - b.getTime());
    return candidats[0] ?? null;
}
function ajouterJours(date, jours) {
    return new Date(Date.parse(`${date}T12:00Z`) + jours * 86_400_000).toISOString().slice(0, 10);
}
export function reservationCreneauActive(r, maintenant) {
    return (r.etat === 'demandee' ||
        r.etat === 'confirmee' ||
        (r.etat === 'en_attente_email' && r.attenteJusquau !== null && r.attenteJusquau > maintenant));
}
const chevauche = (du, au, debut, fin) => du < fin && au > debut;
export function attribuerCreneau(entree, debut) {
    const { type, maintenant } = entree;
    const locale = partiesLocales(maintenant, entree.fuseau);
    const horizonDate = ajouterJours(locale.date, type.horizonJours);
    const horizon = instantLocal(horizonDate, locale.heure, entree.fuseau);
    if (debut.getTime() < maintenant.getTime() + type.preavisHeures * 3_600_000 ||
        (horizon ? debut > horizon : partiesLocales(debut, entree.fuseau).date > horizonDate))
        return null;
    const fin = new Date(debut.getTime() + type.dureeMinutes * 60_000);
    const du = new Date(debut.getTime() - type.tamponAvantMinutes * 60_000);
    const au = new Date(fin.getTime() + type.tamponApresMinutes * 60_000);
    // Les tampons protègent des autres rendez-vous ; une fermeture ne vise que la visite.
    if (entree.exceptions.some((e) => e.sorte === 'fermeture' && chevauche(debut, fin, e.du, e.au)))
        return null;
    const actives = entree.reservations.filter((r) => reservationCreneauActive(r, maintenant) && chevauche(du, au, r.occupeDu, r.occupeAu));
    // Le plafond porte sur la simultanéité réelle, pas sur la somme de visites successives.
    const bornes = [
        du.getTime(),
        ...actives.map((r) => Math.max(du.getTime(), r.occupeDu.getTime())),
    ];
    if (bornes.some((t) => actives.filter((r) => r.occupeDu.getTime() <= t && r.occupeAu.getTime() > t).length >=
        entree.simultanesMax))
        return null;
    const membreId = type.membres.find((id) => entree.membresActifs.includes(id) &&
        !actives.some((r) => r.membreId === id) &&
        !entree.occupations.some((o) => o.membres.includes(id) && chevauche(du, au, o.debut, o.fin)));
    return membreId ? { debut, fin, membreId } : null;
}
export function calculerCreneaux(entree) {
    const { type, du, au, fuseau } = entree;
    const resultat = new Map();
    const retenir = (debut, plageDu, plageAu) => {
        if (debut < du || debut >= au)
            return;
        const creneau = attribuerCreneau(entree, debut);
        // La visite tient dans la plage ; ses tampons peuvent en déborder.
        if (creneau && debut >= plageDu && creneau.fin <= plageAu)
            resultat.set(debut.getTime(), creneau);
    };
    let date = partiesLocales(du, fuseau).date;
    const dernierJour = partiesLocales(new Date(au.getTime() - 1), fuseau).date;
    for (; date <= dernierJour; date = ajouterJours(date, 1)) {
        const jour = new Date(`${date}T12:00Z`).getUTCDay() || 7;
        for (const plage of type.plages.filter((p) => p.jour === jour)) {
            // Les bornes et les débuts sont parcourus en heures civiles pour ne pas doubler l'automne.
            const borne = (heure) => {
                const minute = Number(heure.slice(0, 2)) * 60 + Number(heure.slice(3));
                for (let m = minute; m < 1440; m++) {
                    const instant = instantLocal(date, `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`, fuseau);
                    if (instant)
                        return instant;
                }
                return null;
            };
            const plageDu = borne(plage.debut);
            const plageAu = borne(plage.fin);
            if (!plageDu || !plageAu)
                continue;
            const minutes = (h) => Number(h.slice(0, 2)) * 60 + Number(h.slice(3));
            for (let m = minutes(plage.debut); m < minutes(plage.fin); m += type.pasMinutes) {
                const debut = instantLocal(date, `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`, fuseau);
                if (debut)
                    retenir(debut, plageDu, plageAu);
            }
        }
    }
    for (const ouverture of entree.exceptions.filter((e) => e.sorte === 'ouverture')) {
        const premiere = partiesLocales(ouverture.du, fuseau);
        const fin = partiesLocales(ouverture.au, fuseau);
        for (let jour = premiere.date; jour <= fin.date; jour = ajouterJours(jour, 1)) {
            const premier = jour === premiere.date
                ? Number(premiere.heure.slice(0, 2)) * 60 + Number(premiere.heure.slice(3))
                : 0;
            for (let m = premier; m < 1440; m += type.pasMinutes) {
                const debut = instantLocal(jour, `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`, fuseau);
                if (debut && debut >= ouverture.du && debut < ouverture.au)
                    retenir(debut, ouverture.du, ouverture.au);
            }
        }
    }
    return [...resultat.values()].sort((a, b) => a.debut.getTime() - b.debut.getTime());
}
export function creneauValide(entree, debut) {
    return (calculerCreneaux({ ...entree, du: debut, au: new Date(debut.getTime() + 1) }).find((c) => c.debut.getTime() === debut.getTime()) ?? null);
}
//# sourceMappingURL=prise-rendez-vous.js.map